import React, { useEffect, useState } from 'react';
import Icon from 'src/components/common/Icon';
import { parseCustomerEmail } from './Transactions.helpers';
import { SearchError, SearchPill } from './Transactions.styles';

const PLACEHOLDER = 'cliente@correo.com';
const LABEL = 'Filtrar por correo del cliente';
const INVALID_COPY = 'Ingresa un correo electrónico válido';

interface CustomerEmailSearchProps {
    /** Correo con el que el listado está filtrado; vacío es sin filtro. */
    value: string;
    onSearch: (customerEmail: string) => void;
    onClear: () => void;
    disabled?: boolean;
    disabledHint?: string;
}

/**
 * Filtro de correo del listado de transacciones.
 *
 * Vive dentro de la pestaña y no reusa el buscador central de /retention: ese
 * buscador es el cliente de TODAS las pestañas (carrito, vigencias, etc.). Este
 * campo solo acota GET commercial_panel/transactions vía `customer_email`.
 *
 * Visible en Estado A y Estado B. Vaciar y buscar vuelve a la cartera; un
 * texto que no es correo se marca en lugar de consultar. No comprueba que el
 * cliente exista: un correo sin filas es el vacío del listado.
 */
const CustomerEmailSearch = ({
    value,
    onSearch,
    onClear,
    disabled = false,
    disabledHint,
}: CustomerEmailSearchProps) => {
    const [text, setText] = useState(value);
    const [invalid, setInvalid] = useState(false);

    useEffect(() => {
        setText(value);
        setInvalid(false);
    }, [value]);

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        if (disabled) return;

        const parsed = parseCustomerEmail(text);
        if (parsed === null) {
            setInvalid(true);
            return;
        }

        setInvalid(false);
        if (parsed === '') {
            onClear();
            return;
        }
        onSearch(parsed);
    };

    const handleClear = () => {
        if (disabled) return;
        setText('');
        setInvalid(false);
        if (value !== '') onClear();
    };

    return (
        <span title={disabled ? disabledHint : undefined}>
            <SearchPill
                $invalid={invalid}
                $wide
                $disabled={disabled}
                onSubmit={handleSubmit}
                role="search"
            >
                <span className="search-icon">
                    <Icon icon="pcom-icon-email" size={13} />
                </span>
                <input
                    type="text"
                    autoComplete="off"
                    data-id="transactions-email-input"
                    aria-label={LABEL}
                    placeholder={PLACEHOLDER}
                    value={text}
                    disabled={disabled}
                    onChange={(event) => {
                        setText(event.target.value);
                        if (invalid) setInvalid(false);
                    }}
                />
                {text !== '' && (
                    <button
                        type="button"
                        className="search-action"
                        data-id="transactions-email-clear"
                        aria-label="Quitar el filtro de correo"
                        disabled={disabled}
                        onClick={handleClear}
                    >
                        <Icon icon="pcom-icon-close" size={11} />
                    </button>
                )}
                <button
                    type="submit"
                    className="search-action"
                    data-id="transactions-email-submit"
                    aria-label={LABEL}
                    disabled={disabled}
                >
                    <Icon icon="pcom-icon-arrow-right" size={12} />
                </button>
            </SearchPill>
            {invalid && (
                <SearchError role="alert" data-id="transactions-email-error">
                    {INVALID_COPY}
                </SearchError>
            )}
        </span>
    );
};

export default CustomerEmailSearch;
